import { useCallback, useRef } from 'react';
import {
  EAutoRange,
  EAxisAlignment,
  ECoordinateMode,
  EHorizontalAnchorPoint,
  EVerticalAnchorPoint,
  FastLineRenderableSeries,
  HorizontalLineAnnotation,
  NativeTextAnnotation,
  NumberRange,
  NumericAxis,
  RolloverModifier,
  type SciChartSurface,
  VerticalLineAnnotation,
  XyDataSeries,
} from 'scichart';
import {
  SciChartView,
  type SciChartInitContext,
} from 'components/charts/scichart';
import { fmtUsd } from './interest-modeling-format';
import type { ElectionModel } from './interest-modeling-election';
import styles from './election-results.module.scss';

const TICK_COLOR = '#525252';

export interface ElectionChartProps {
  /** The comparison model — one cumulative net-to-owner line per variant on a shared axis. */
  model: ElectionModel;
  /** Shows the SciChartView loading overlay while a reprice is in flight. */
  isLoading: boolean;
}

/**
 * The UMI election comparison chart (three-way outcome comparison) — a SciChart
 * port of the PM mock's ElectionChart onto the repo `SciChartView` wrapper (which owns
 * the WASM lifecycle, loading overlay, reset-zoom, and image export). One cumulative
 * net-to-owner line per election, every line anchored at (month 0, $0) so the up-front
 * lease bonus reads as a steep initial jump while the cost-bearing options (participate /
 * pre-payout carried) dip below zero from the same origin. `initChart` builds the
 * project-month X axis, the cumulative-net Y axis (adaptive-currency labels, both-ends
 * headroom for the drawdown), and the rollover tooltip ONCE; `renderData` rebuilds the
 * lines + zero baseline + per-variant payout markers on every data change. A legend below
 * mirrors the drawn series (colors match the outcome cards).
 *
 * @param {ElectionChartProps} props - The comparison model + loading flag.
 * @return {JSX.Element} The chart card.
 */
export function ElectionChart({ model, isLoading }: ElectionChartProps) {
  // The stable init/render callbacks read the latest model via a ref.
  const modelRef = useRef<ElectionModel>(model);
  modelRef.current = model;

  const initChart = useCallback(
    ({ surface, wasmContext }: SciChartInitContext) => {
      const xAxis = new NumericAxis(wasmContext, {
        axisTitle: 'Project month',
        axisTitleStyle: { fontSize: 12, color: TICK_COLOR },
        labelStyle: { fontSize: 10, color: TICK_COLOR },
        autoTicks: false,
        majorDelta: 12,
        minorDelta: 12,
        drawMinorGridLines: false,
        drawMinorTickLines: false,
        drawMajorBands: false,
        growBy: new NumberRange(0.02, 0.02),
        autoRange: EAutoRange.Always,
        axisAlignment: EAxisAlignment.Bottom,
      });
      const yAxis = new NumericAxis(wasmContext, {
        axisTitle: 'Cumulative net',
        axisTitleStyle: { fontSize: 12, color: TICK_COLOR },
        labelStyle: { fontSize: 10, color: TICK_COLOR },
        drawMajorBands: false,
        minorsPerMajor: 1,
        // Net-to-owner runs negative (drawdown) then positive, so leave headroom both ends.
        growBy: new NumberRange(0.08, 0.08),
        autoRange: EAutoRange.Always,
        axisAlignment: EAxisAlignment.Left,
      });
      yAxis.labelProvider.formatLabel = (value: number) => fmtUsd(value);
      surface.xAxes.add(xAxis);
      surface.yAxes.add(yAxis);

      surface.chartModifiers.add(
        new RolloverModifier({
          showRolloverLine: true,
          rolloverLineStroke: '#8d8d8d',
          snapToDataPoint: true,
          tooltipDataTemplate: (seriesInfo) => [
            `${seriesInfo.seriesName}: ${fmtUsd(seriesInfo.yValue)}`,
          ],
        })
      );
    },
    []
  );

  const renderData = useCallback(
    ({ surface, wasmContext }: SciChartInitContext) => {
      const draw = modelRef.current;
      surface.renderableSeries.clear();
      surface.annotations.clear();

      // Anchor every line at (month 0, $0): prepend the origin to the shared month axis.
      const months = [0, ...draw.months];
      draw.lines.forEach((line) => {
        const series = new FastLineRenderableSeries(wasmContext, {
          dataSeries: new XyDataSeries(wasmContext, {
            xValues: months,
            yValues: [0, ...line.values],
            dataSeriesName: line.label,
          }),
          stroke: line.color,
          strokeThickness: 2.5,
        });
        styleTooltip(series, line.color);
        surface.renderableSeries.add(series);
      });

      surface.annotations.add(
        new HorizontalLineAnnotation({
          y1: 0,
          stroke: '#a8a8a8',
          strokeThickness: 1,
        })
      );

      // Each variant that reverts flips at its own dashed line, label running up the line.
      draw.lines.forEach((line) => {
        if (line.payoutMonth === null) {
          return;
        }
        addVerticalMarker(
          surface,
          line.payoutMonth,
          `${line.label.split(' (')[0]} payout`,
          line.color
        );
      });
    },
    []
  );

  return (
    <div className={styles.chartCard}>
      <SciChartView
        initChart={initChart}
        renderData={renderData}
        renderDeps={[model]}
        isLoading={isLoading}
        height={440}
        showResetZoom
        exportConfig={{
          label: 'Election comparison',
          filenamePrefix: 'election-comparison',
        }}
      />
      <div className={styles.legend}>
        {model.lines.map((line) => (
          <span key={line.variant} className={styles.legendItem}>
            <span
              className={styles.legendSwatch}
              style={{ background: line.color }}
            />
            {line.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// White tooltip text on the series' own color for a legible rollover (mock parity).
function styleTooltip(
  series: {
    rolloverModifierProps: { tooltipColor: string; tooltipTextColor: string };
  },
  color: string
) {
  series.rolloverModifierProps.tooltipColor = color;
  series.rolloverModifierProps.tooltipTextColor = '#ffffff';
}

// A dashed vertical line with a 90°-rotated label running up the line near the top of the
// plot (mock parity — the old Highcharts plot-line labels).
function addVerticalMarker(
  surface: SciChartSurface,
  x: number,
  label: string,
  color: string
) {
  surface.annotations.add(
    new VerticalLineAnnotation({
      x1: x,
      stroke: color,
      strokeThickness: 1,
      strokeDashArray: [6, 4],
      showLabel: false,
    })
  );
  surface.annotations.add(
    new NativeTextAnnotation({
      x1: x,
      y1: 0.02,
      xCoordinateMode: ECoordinateMode.DataValue,
      yCoordinateMode: ECoordinateMode.Relative,
      text: label,
      textColor: color,
      fontSize: 10,
      rotation: 90,
      horizontalAnchorPoint: EHorizontalAnchorPoint.Left,
      verticalAnchorPoint: EVerticalAnchorPoint.Bottom,
    })
  );
}
